import { useTranslation } from "react-i18next";
import {
  missingRequiredInputFields,
  normalizeInputFields,
  effectiveInputFields,
} from "./checklistInputFields";
import { itemOutcomeKey } from "./checklistResponseConfig";

export function ChecklistItemCustomFields({
  fields = [],
  values = {},
  disabled = false,
  onChange,
  showErrors = false,
  item,
}) {
  const { t } = useTranslation();
  const defs = item ? effectiveInputFields(fields, itemOutcomeKey(item)) : normalizeInputFields(fields);
  if (!defs.length) return null;

  const missing = showErrors || Boolean(item && itemOutcomeKey(item) !== "pending")
    ? missingRequiredInputFields(defs, values)
    : [];

  const handleChange = (key, value) => {
    if (!onChange) return;
    onChange({ ...values, [key]: value });
  };

  const handleBlur = (key, value) => {
    if (!onChange) return;
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed === value) return;
    onChange({ ...values, [key]: trimmed });
  };

  return (
    <div className="mt-2 space-y-2">
      <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {t("quality.checklist.item.customFieldsTitle")}
      </p>
      {defs.map((field) => {
        const value = values?.[field.key] ?? "";
        const hasError = missing.some((m) => m.key === field.key);
        const commonClass = `w-full rounded-md border px-2.5 py-1.5 text-sm ${
          hasError
            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
            : "border-gray-300 focus:border-teal-500 focus:ring-teal-500"
        }`;

        return (
          <div key={field.key}>
            <label className="mb-1 flex items-baseline gap-1 text-xs font-medium text-gray-700">
              <span>{field.label}</span>
              {field.required && (
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
              )}
              {field.unit && (
                <span className="text-gray-400 font-normal">({field.unit})</span>
              )}
            </label>
            {field.input_type === "textarea" ? (
              <textarea
                rows={2}
                disabled={disabled}
                className={`${commonClass} min-h-[64px] resize-y`}
                placeholder={field.placeholder || ""}
                value={value}
                onChange={(e) => handleChange(field.key, e.target.value)}
                onBlur={(e) => handleBlur(field.key, e.target.value)}
              />
            ) : (
              <input
                type={field.input_type === "number" ? "number" : "text"}
                disabled={disabled}
                className={commonClass}
                placeholder={field.placeholder || ""}
                value={value}
                onChange={(e) => handleChange(field.key, e.target.value)}
                onBlur={(e) => handleBlur(field.key, e.target.value)}
              />
            )}
            {hasError && (
              <p className="mt-0.5 text-xs text-red-600">
                {t("quality.checklist.item.customFieldRequired")}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
